import {FreightType, FreightTypeEntity} from "../../../model/freight-type";
import {compareString, contains} from "../../../util/util";
import {createParamSelector, getUseQueryResultData} from "../../../util/selector-util";
import {CargoSubtype} from "../../../model/cargo-subtype";
import {createSelector} from "reselect";

export type FreightTypeSelectorProps = {
  regionId?: string
  cargoSubtype?: CargoSubtype
  showDoNotUse?: boolean;
};


const getRegionIdParam = createParamSelector<FreightTypeSelectorProps | undefined, string | undefined>(params => params?.regionId)
const getCargoSubtypeParam = createParamSelector<FreightTypeSelectorProps | undefined, CargoSubtype | undefined>(params => params?.cargoSubtype)
const getShowDoNotUseParam = createParamSelector<FreightTypeSelectorProps | undefined, boolean | undefined>(params => params?.showDoNotUse)

export const freightTypesSelector = () => createSelector(
  [getUseQueryResultData<FreightType[]>, getRegionIdParam, getCargoSubtypeParam, getShowDoNotUseParam],
  (data, regionId, cargoSubtype, showDoNotUse) => {
    return selectFreightTypesAndTransform(
      data ?? [], {
        regionId: regionId,
        cargoSubtype: cargoSubtype,
        showDoNotUse: showDoNotUse
      }
    );
  }
);

export const freightTypeCategoriesSelector = () => createSelector(
  [getUseQueryResultData<FreightType[]>, getRegionIdParam, getCargoSubtypeParam, getShowDoNotUseParam],
  (data, regionId, cargoSubtype, showDoNotUse) => {
    const freightTypes = selectFreightTypesAndTransform(
      data ?? [], {
        regionId: regionId,
        cargoSubtype: cargoSubtype,
        showDoNotUse: showDoNotUse
      }
    );

    const freightTypeCategories = freightTypes
      .map(ft => ft.category)
      .filter((ft): ft is string => !!ft)
      .sort(compareString)

    return [...new Set(freightTypeCategories)]
  }
);

export const selectFreightTypes = (values?: FreightType[], selector?: FreightTypeSelectorProps): FreightType[] => {
  if (!values) {
    return [];
  }

  return values.filter((v) => {
    let filterIn = true;

    if (selector?.regionId) {
      if (!contains(v.regionIds, id => id === selector.regionId)) {
        filterIn = false;
      }
    }

    if (filterIn && selector?.cargoSubtype) {
      if (!v.cargoSubType || v.cargoSubType !== selector.cargoSubtype?.code) {
        filterIn = false;
      }
    }

    const showDoNotUse = selector?.showDoNotUse ?? false;
    if (filterIn && !showDoNotUse && v.doNotUse) {
      filterIn = false;
    }

    return filterIn;
  });
};

export const selectFreightTypesAndTransform = (values?: FreightType[], selector?: FreightTypeSelectorProps): FreightTypeEntity[] => {
  const selected = selectFreightTypes(values, selector);
  return selected.map(m => FreightTypeEntity.from(m));
};

export const selectFreightTypeCategories = (values?: FreightType[], selector?: FreightTypeSelectorProps): string[] => {
  const freightTypes = selectFreightTypes(values, selector);
  const mappedCategories = freightTypes
    .map((v) => v.category)
    .filter((v): v is string => !!v)
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  return [...new Set(mappedCategories)];
};
